import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/ApiService';
import { isTypeCompatible, getIncompatibilityReason } from './typeCompatibility';
import { ErpBinding, SalesforceBinding } from '../ModelDataFields/Input/reusables/ErpBindingButton';
import { saveBindingNavigationState, loadBindingNavigationState } from '../FieldBindingModal';
import { castToAppType, formatValueForDisplay, areValuesEqual } from './typeCasting';
import TailwindModal from '../Tailwind/TailwindModal';
import { cn } from '../../lib/utils';

interface SalesforceField {
    name: string;
    label: string;
    type: string;
    length?: number;
    nillable?: boolean;
    createable?: boolean;
    updateable?: boolean;
    picklistValues?: any[];
    referenceTo?: string[];
}

type FilterOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'in' | 'not_in';

interface SalesforceFilter {
    field: string;
    operator: FilterOperator;
    value: string | string[];
}

type AppFieldType = 'str' | 'int' | 'float' | 'datetime' | 'bool';

interface SalesforceNavigationProps {
    connectionId: number;
    onFieldSelect?: (field: SalesforceField, objectType: string) => void;
    appFieldType?: string;
    bindingKey?: string;
    valueId?: number;
    scenarioId?: number;
    existingBinding?: ErpBinding | null;
    onBindingCreated?: () => void;
    onBindingDeleted?: () => void;
    currentValue?: any;
    onSyncApply?: (erpValue: any) => void;
    onSkipApply?: () => void;
}

const Spinner = ({ className }: { className?: string }) => (
    <div className={cn('border-2 border-slate-200 border-t-slate-700 rounded-full animate-spin', className)} />
);

const selectCls = 'w-full appearance-none bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed';

const operatorLabels: Record<FilterOperator, string> = {
    'eq': 'Equals',
    'ne': 'Not Equals',
    'gt': 'Greater than',
    'gte': 'Greater / Equal',
    'lt': 'Less than',
    'lte': 'Less / Equal',
    'like': 'Like',
    'in': 'In',
    'not_in': 'Not In',
};

export default function SalesforceNavigation({
    connectionId,
    onFieldSelect,
    appFieldType,
    bindingKey,
    valueId,
    scenarioId,
    existingBinding,
    onBindingCreated,
    onBindingDeleted,
    currentValue,
    onSyncApply,
    onSkipApply,
}: SalesforceNavigationProps) {
    const [objectTypes, setObjectTypes] = useState<string[]>([]);
    const [selectedObjectType, setSelectedObjectType] = useState<string>('');
    const [fields, setFields] = useState<SalesforceField[]>([]);
    const [loadingObjectTypes, setLoadingObjectTypes] = useState(false);
    const [loadingFields, setLoadingFields] = useState(false);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [records, setRecords] = useState<any[]>([]);
    const [filters, setFilters] = useState<SalesforceFilter[]>([]);
    const [selectedFields, setSelectedFields] = useState<string[]>([]);
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
    const [done, setDone] = useState(true);
    const [totalSize, setTotalSize] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const resultsRef = useRef<HTMLDivElement>(null);
    const compatibilityRef = useRef<HTMLDivElement>(null);
    const successRef = useRef<HTMLDivElement>(null);
    const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [creatingBinding, setCreatingBinding] = useState(false);
    const [bindingError, setBindingError] = useState<string | null>(null);
    const [bindingSuccess, setBindingSuccess] = useState(false);
    const [isInitializingBinding, setIsInitializingBinding] = useState(false);
    const [deletingBinding, setDeletingBinding] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const effectiveAppFieldType: AppFieldType | undefined =
        (bindingKey === 'start_date' || bindingKey === 'end_date')
            ? 'datetime'
            : (appFieldType as AppFieldType | undefined);

    useEffect(() => {
        loadObjectTypes();
    }, [connectionId]);

    const initializeFromBinding = async (sfBinding: SalesforceBinding) => {
        setIsInitializingBinding(true);
        try {
            setSelectedObjectType(sfBinding.sf_object);
            const fieldsResponse = await apiService.getSalesforceFields(connectionId, sfBinding.sf_object);
            if (fieldsResponse && fieldsResponse.ok) {
                const fieldsData = await fieldsResponse.json();
                const fieldsList = Array.isArray(fieldsData) ? fieldsData : [];
                setFields(fieldsList);
                const defaultFields = fieldsList.slice(0, 5).map((f: SalesforceField) => f.name);
                if (sfBinding.sf_field && !defaultFields.includes(sfBinding.sf_field)) {
                    defaultFields.push(sfBinding.sf_field);
                }
                setSelectedFields(defaultFields);
                const recordFilter: SalesforceFilter = { field: 'Id', operator: 'eq', value: sfBinding.sf_record_id };
                setFilters([recordFilter]);
                const recordsResponse = await apiService.querySalesforceRecords(
                    connectionId, sfBinding.sf_object, [recordFilter], defaultFields, pageSize, 100, undefined
                );
                if (recordsResponse && recordsResponse.ok) {
                    const recordsData = await recordsResponse.json();
                    const loadedRecords = recordsData.records || [];
                    setRecords(loadedRecords);
                    setNextCursor(recordsData.next_cursor);
                    setDone(recordsData.done || false);
                    setTotalSize(recordsData.total_size || 0);
                    const boundRecord = loadedRecords.find((r: any) => r.Id === sfBinding.sf_record_id);
                    if (boundRecord && sfBinding.sf_field) {
                        setSelectedRecord(boundRecord);
                        setSelectedField(sfBinding.sf_field);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to initialize from existing binding:', error);
        } finally {
            setIsInitializingBinding(false);
        }
    };

    const initializeFromSavedState = async () => {
        const savedState = loadBindingNavigationState();
        if (!savedState || savedState.connectionId !== connectionId) return;
        if (!savedState.objectType || !objectTypes.includes(savedState.objectType)) return;
        setIsInitializingBinding(true);
        try {
            setSelectedObjectType(savedState.objectType);
            const fieldsResponse = await apiService.getSalesforceFields(connectionId, savedState.objectType);
            if (fieldsResponse && fieldsResponse.ok) {
                const fieldsData = await fieldsResponse.json();
                const fieldsList = Array.isArray(fieldsData) ? fieldsData : [];
                setFields(fieldsList);
                const validFields = savedState.selectedFields.filter(
                    (f: string) => fieldsList.some((field: SalesforceField) => field.name === f)
                );
                setSelectedFields(validFields.length > 0 ? validFields : fieldsList.slice(0, 5).map((f: SalesforceField) => f.name));
                const validFilters = savedState.filters.filter(
                    (f: any) => fieldsList.some((field: SalesforceField) => field.name === f.field)
                );
                setFilters(validFilters);
                if (savedState.pageSize) setPageSize(savedState.pageSize);
            }
        } catch (error) {
            console.error('Failed to initialize from saved state:', error);
        } finally {
            setIsInitializingBinding(false);
        }
    };

    useEffect(() => {
        if (existingBinding && existingBinding.type === 'salesforce' && objectTypes.length > 0) {
            const sfBinding = existingBinding as SalesforceBinding;
            if (sfBinding.sf_object && objectTypes.includes(sfBinding.sf_object)) {
                initializeFromBinding(sfBinding);
            }
        } else if (!existingBinding && objectTypes.length > 0) {
            initializeFromSavedState();
        }
    }, [existingBinding, objectTypes]);

    useEffect(() => {
        if (records.length > 0 && resultsRef.current && !loadingRecords) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [records, loadingRecords]);

    useEffect(() => {
        if (bindingSuccess && successRef.current) {
            setTimeout(() => {
                successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
    }, [bindingSuccess]);

    useEffect(() => {
        if (isInitializingBinding) return;
        if (selectedObjectType) {
            loadFields(selectedObjectType);
            setRecords([]);
            setFilters([]);
            setSelectedFields([]);
            setNextCursor(undefined);
            setDone(true);
            setTotalSize(0);
            setCursorHistory([]);
            setCurrentPage(0);
        } else {
            setFields([]);
            setRecords([]);
        }
    }, [selectedObjectType]);

    const loadObjectTypes = async () => {
        setLoadingObjectTypes(true);
        try {
            const response = await apiService.getSalesforceObjectTypes(connectionId);
            if (response && response.ok) {
                const data = await response.json();
                setObjectTypes(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Failed to load Salesforce object types:', error);
        }
        setLoadingObjectTypes(false);
    };

    const loadFields = async (objectType: string) => {
        setLoadingFields(true);
        try {
            const response = await apiService.getSalesforceFields(connectionId, objectType);
            if (response && response.ok) {
                const data = await response.json();
                const fieldsList = Array.isArray(data) ? data : [];
                setFields(fieldsList);
                if (fieldsList.length > 0) {
                    setSelectedFields(fieldsList.slice(0, 5).map(f => f.name));
                }
            }
        } catch (error) {
            console.error('Failed to load Salesforce fields:', error);
        }
        setLoadingFields(false);
    };

    const handleObjectTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedObjectType(e.target.value);
    };

    const handleAddFilter = () => {
        setFilters([...filters, { field: '', operator: 'eq', value: '' }]);
    };

    const handleRemoveFilter = (index: number) => {
        setFilters(filters.filter((_, i) => i !== index));
    };

    const getOperatorsForFieldType = (fieldType: string): FilterOperator[] => {
        switch (fieldType) {
            case 'boolean': return ['eq', 'ne'];
            case 'date': case 'datetime': case 'time':
                return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'];
            case 'int': case 'double': case 'currency': case 'percent': case 'long':
                return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'not_in'];
            default:
                return ['eq', 'ne', 'like', 'in', 'not_in'];
        }
    };

    const handleFilterChange = (index: number, key: keyof SalesforceFilter, value: string | FilterOperator) => {
        const newFilters = [...filters];
        if (key === 'field') {
            const sf = fields.find(f => f.name === value);
            const allowed = sf ? getOperatorsForFieldType(sf.type) : ['eq'];
            newFilters[index].field = value as string;
            newFilters[index].operator = allowed[0] as FilterOperator;
            newFilters[index].value = '';
        } else if (key === 'operator') {
            newFilters[index].operator = value as FilterOperator;
            newFilters[index].value = '';
        } else if (key === 'value') {
            const sf = fields.find(f => f.name === newFilters[index].field);
            const fieldType = sf?.type || 'string';
            const operator = newFilters[index].operator;
            if (fieldType === 'boolean') {
                newFilters[index].value = value === 'true';
            } else if (fieldType === 'int' || fieldType === 'long') {
                if (operator === 'in' || operator === 'not_in') {
                    newFilters[index].value = value ? (value as string).split(',').map(v => parseInt(v.trim(), 10)).filter(n => !isNaN(n)) : [];
                } else {
                    const n = parseInt(value as string, 10);
                    newFilters[index].value = isNaN(n) ? value : n;
                }
            } else if (fieldType === 'double' || fieldType === 'currency' || fieldType === 'percent') {
                if (operator === 'in' || operator === 'not_in') {
                    newFilters[index].value = value ? (value as string).split(',').map(v => parseFloat(v.trim())).filter(n => !isNaN(n)) : [];
                } else {
                    const n = parseFloat(value as string);
                    newFilters[index].value = isNaN(n) ? value : n;
                }
            } else if (operator === 'in' || operator === 'not_in') {
                newFilters[index].value = value ? (value as string).split(',').map(v => v.trim()) : [];
            } else {
                newFilters[index].value = value as string;
            }
        } else {
            newFilters[index][key] = value as any;
        }
        setFilters(newFilters);
    };

    const handleFieldToggle = (fieldName: string) => {
        setSelectedFields(prev =>
            prev.includes(fieldName) ? prev.filter(f => f !== fieldName) : [...prev, fieldName]
        );
    };

    const loadRecords = async (useCursor?: string, isNewQuery: boolean = false) => {
        if (!selectedObjectType) return;
        const fieldsToQuery = selectedFields.length > 0 ? selectedFields : fields.slice(0, 5).map(f => f.name);
        setLoadingRecords(true);
        try {
            const response = await apiService.querySalesforceRecords(
                connectionId, selectedObjectType, filters, fieldsToQuery, pageSize, 100, useCursor
            );
            if (response && response.ok) {
                const data = await response.json();
                setRecords(data.records || []);
                setNextCursor(data.next_cursor);
                setDone(data.done || false);
                setTotalSize(data.total_size || 0);
                if (isNewQuery) { setCursorHistory([]); setCurrentPage(0); }
            }
        } catch (error) {
            console.error('Failed to load Salesforce records:', error);
        }
        setLoadingRecords(false);
    };

    const handleNext = () => {
        if (nextCursor) {
            setCursorHistory(prev => [...prev, cursorHistory[currentPage] || undefined]);
            setCurrentPage(prev => prev + 1);
            loadRecords(nextCursor);
        }
    };

    const handlePrevious = () => {
        if (currentPage > 0) {
            const previousCursor = cursorHistory[currentPage - 1];
            setCurrentPage(prev => prev - 1);
            setCursorHistory(prev => prev.slice(0, -1));
            loadRecords(previousCursor);
        }
    };

    const handleCellClick = (record: any, fieldName: string) => {
        setSelectedRecord(record);
        setSelectedField(fieldName);
        setBindingError(null);
        setBindingSuccess(false);
        setTimeout(() => {
            if (compatibilityRef.current) {
                compatibilityRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
    };

    const handleCreateBinding = async () => {
        if (!selectedRecord || !selectedField || !valueId || !scenarioId) {
            setBindingError('Missing required data for binding creation');
            return;
        }
        setCreatingBinding(true);
        setBindingError(null);
        setBindingSuccess(false);
        try {
            let response;
            if (existingBinding) {
                response = await apiService.updateSalesforceBinding(
                    existingBinding.id, selectedObjectType, selectedRecord.Id, selectedField
                );
            } else {
                response = await apiService.createSalesforceBinding(
                    selectedObjectType, selectedRecord.Id, selectedField, valueId, connectionId, scenarioId, bindingKey || null
                );
            }
            if (!response) { setBindingError('Request failed. Please check your connection and try again.'); return; }
            if (response.ok) {
                setBindingSuccess(true);
                if (!existingBinding) {
                    saveBindingNavigationState({ connectionId, objectType: selectedObjectType, filters, selectedFields, pageSize });
                }
                onBindingCreated?.();
            } else {
                let errorMessage = existingBinding ? 'Failed to update binding' : 'Failed to create binding';
                try {
                    const errorData = await response.json();
                    if (errorData && errorData.detail) {
                        errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
                    }
                } catch { errorMessage = response.statusText || `Error ${response.status}`; }
                setBindingError(errorMessage);
            }
        } catch (error) {
            console.error('Failed to create/update binding:', error);
            setBindingError(error instanceof Error ? error.message : 'An error occurred while creating the binding');
        } finally {
            setCreatingBinding(false);
        }
    };

    const handleDeleteBinding = async () => {
        if (!existingBinding) return;
        setDeletingBinding(true);
        setBindingError(null);
        setDeleteSuccess(false);
        try {
            const response = await apiService.deleteErpBinding(existingBinding.id, bindingKey);
            if (!response) { setBindingError('Request failed. Please check your connection and try again.'); return; }
            if (response.ok) {
                setDeleteSuccess(true);
                onBindingDeleted?.();
            } else {
                let errorMessage = 'Failed to delete binding';
                try {
                    const errorData = await response.json();
                    if (errorData && errorData.detail) {
                        errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
                    }
                } catch { errorMessage = response.statusText || `Error ${response.status}`; }
                setBindingError(errorMessage);
            }
        } catch (error) {
            console.error('Failed to delete binding:', error);
            setBindingError(error instanceof Error ? error.message : 'An error occurred while deleting the binding');
        } finally {
            setDeletingBinding(false);
        }
    };

    const displayFields = selectedFields.length > 0 ? selectedFields : fields.slice(0, 5).map(f => f.name);

    if (isInitializingBinding) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
                <Spinner className="w-8 h-8" />
                <p className="text-sm text-slate-500">Loading binding configuration...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* Object Type Select */}
            <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                    Salesforce Object
                </label>
                <div className="relative max-w-sm">
                    <select
                        value={selectedObjectType}
                        onChange={handleObjectTypeChange}
                        disabled={loadingObjectTypes}
                        className={selectCls}
                    >
                        <option value="">Select an object...</option>
                        {objectTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400">
                        {loadingObjectTypes
                            ? <Spinner className="w-4 h-4" />
                            : <span className="material-symbols-outlined text-[20px]">expand_more</span>
                        }
                    </span>
                </div>
            </div>

            {selectedObjectType && !loadingFields && (
                <>
                    <hr className="border-slate-200" />

                    {/* Fields to display */}
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Fields to Display</p>
                        <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                            {fields.map(field => (
                                <button
                                    key={field.name}
                                    onClick={() => handleFieldToggle(field.name)}
                                    className={cn(
                                        'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
                                        selectedFields.includes(field.name)
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                    )}
                                >
                                    {field.label}
                                </button>
                            ))}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2">
                            {selectedFields.length > 0
                                ? `${selectedFields.length} field${selectedFields.length !== 1 ? 's' : ''} selected`
                                : 'No fields selected'}
                        </p>
                    </div>

                    <hr className="border-slate-200" />

                    {/* Filters */}
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-slate-700">
                                Filters {filters.length > 0 && `(${filters.length})`}
                            </p>
                            <button
                                onClick={handleAddFilter}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[14px]">add</span>
                                Add Filter
                            </button>
                        </div>

                        {filters.length > 0 ? (
                            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
                                {filters.map((filter, index) => {
                                    const sf = fields.find(f => f.name === filter.field);
                                    const fieldType = sf?.type || 'string';
                                    const allowedOps = sf ? getOperatorsForFieldType(sf.type) : ['eq', 'ne', 'like', 'in', 'not_in'];

                                    return (
                                        <div key={index} className="flex gap-2 items-center">
                                            {/* Field */}
                                            <div className="relative flex-[0.3] min-w-0">
                                                <select
                                                    value={filter.field}
                                                    onChange={e => handleFilterChange(index, 'field', e.target.value)}
                                                    className={cn(selectCls, 'text-xs py-1.5')}
                                                >
                                                    <option value="">Field...</option>
                                                    {fields.map(f => <option key={f.name} value={f.name}>{f.label}</option>)}
                                                </select>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400">
                                                    <span className="material-symbols-outlined text-[16px]">expand_more</span>
                                                </span>
                                            </div>
                                            {/* Operator */}
                                            <div className="relative flex-[0.2] min-w-0">
                                                <select
                                                    value={filter.operator}
                                                    onChange={e => handleFilterChange(index, 'operator', e.target.value)}
                                                    className={cn(selectCls, 'text-xs py-1.5')}
                                                >
                                                    {allowedOps.map(op => (
                                                        <option key={op} value={op}>{operatorLabels[op]}</option>
                                                    ))}
                                                </select>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400">
                                                    <span className="material-symbols-outlined text-[16px]">expand_more</span>
                                                </span>
                                            </div>
                                            {/* Value */}
                                            {fieldType === 'boolean' ? (
                                                <div className="relative flex-[0.5] min-w-0">
                                                    <select
                                                        value={typeof filter.value === 'boolean' ? String(filter.value) : filter.value as string}
                                                        onChange={e => handleFilterChange(index, 'value', e.target.value)}
                                                        className={cn(selectCls, 'text-xs py-1.5')}
                                                    >
                                                        <option value="true">True</option>
                                                        <option value="false">False</option>
                                                    </select>
                                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400">
                                                        <span className="material-symbols-outlined text-[16px]">expand_more</span>
                                                    </span>
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                                                    onChange={e => handleFilterChange(index, 'value', e.target.value)}
                                                    placeholder={filter.operator === 'in' || filter.operator === 'not_in' ? 'Comma-separated...' : 'Value...'}
                                                    className="flex-[0.5] min-w-0 bg-slate-50 border border-slate-200 text-slate-900 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                                />
                                            )}
                                            <button
                                                onClick={() => handleRemoveFilter(index)}
                                                className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors shrink-0"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 text-center py-2 italic">No filters applied</p>
                        )}
                    </div>

                    {/* Page size + Load */}
                    <div className="flex gap-3 items-center">
                        <div className="relative w-44 shrink-0">
                            <select
                                value={pageSize}
                                onChange={e => setPageSize(Number(e.target.value))}
                                className={cn(selectCls, 'text-xs')}
                            >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                            </select>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400">
                                <span className="material-symbols-outlined text-[18px]">expand_more</span>
                            </span>
                        </div>
                        <button
                            onClick={() => loadRecords(undefined, true)}
                            disabled={loadingRecords}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loadingRecords ? (
                                <>
                                    <Spinner className="w-4 h-4" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-[18px]">search</span>
                                    Load Records
                                </>
                            )}
                        </button>
                    </div>

                    {/* Records Table */}
                    {loadingRecords && records.length === 0 ? (
                        <div className="flex justify-center py-10">
                            <Spinner className="w-8 h-8" />
                        </div>
                    ) : records.length > 0 ? (
                        <>
                            <div className="relative">
                                {/* Pagination loading overlay */}
                                {loadingRecords && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg z-10">
                                        <Spinner className="w-8 h-8" />
                                    </div>
                                )}

                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold text-slate-700">
                                        Records ({records.length}{totalSize > 0 ? ` of ${totalSize}` : ''})
                                    </p>
                                    <p className="text-xs text-slate-400 italic">Click on any cell to select</p>
                                </div>

                                {/* Table */}
                                <div className="overflow-auto max-h-96 rounded-lg border border-slate-200">
                                    <table className="min-w-full divide-y divide-slate-200">
                                        <thead className="bg-slate-50 sticky top-0 z-[1]">
                                            <tr>
                                                {displayFields.map(fieldName => {
                                                    const f = fields.find(f => f.name === fieldName);
                                                    return (
                                                        <th
                                                            key={fieldName}
                                                            className="px-3 py-2 text-left text-[11px] font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap"
                                                        >
                                                            {f?.label || fieldName}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 bg-white">
                                            {records.map((record, ri) => (
                                                <tr key={ri} className="odd:bg-slate-50/50">
                                                    {displayFields.map(fieldName => {
                                                        let displayValue = '-';
                                                        const value = record[fieldName];
                                                        if (value !== null && value !== undefined) {
                                                            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
                                                                displayValue = new Date(value).toLocaleString('en-US', {
                                                                    year: 'numeric', month: 'short', day: 'numeric',
                                                                    hour: '2-digit', minute: '2-digit'
                                                                });
                                                            } else {
                                                                displayValue = String(value);
                                                            }
                                                        }
                                                        const isSelected = selectedRecord?.Id === record.Id && selectedField === fieldName;
                                                        return (
                                                            <td
                                                                key={fieldName}
                                                                onClick={() => handleCellClick(record, fieldName)}
                                                                className={cn(
                                                                    'px-3 py-2 text-xs max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer transition-colors',
                                                                    isSelected
                                                                        ? 'bg-primary/10 ring-2 ring-inset ring-primary text-primary font-medium'
                                                                        : 'hover:bg-primary/5 text-slate-700'
                                                                )}
                                                                title={displayValue}
                                                            >
                                                                {displayValue}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Pagination */}
                            <div ref={resultsRef} className="flex items-center justify-between px-1">
                                <p className="text-xs text-slate-400">
                                    {totalSize > 0 ? `${totalSize} total records` : ''}
                                </p>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={handlePrevious}
                                        disabled={loadingRecords || currentPage === 0}
                                        className="flex items-center gap-0.5 px-2 py-1 rounded text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">chevron_left</span>
                                        Prev
                                    </button>
                                    <span className="px-2.5 py-0.5 bg-slate-900 text-white text-xs font-semibold rounded min-w-[28px] text-center">
                                        {currentPage + 1}
                                    </span>
                                    {!done && totalSize > 0 && (
                                        <span className="text-xs text-slate-400">/ ~{Math.ceil(totalSize / pageSize)}</span>
                                    )}
                                    <button
                                        onClick={handleNext}
                                        disabled={loadingRecords || done}
                                        className="flex items-center gap-0.5 px-2 py-1 rounded text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Next
                                        <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                    </button>
                                </div>
                            </div>

                            {/* Selected cell + compatibility */}
                            {selectedRecord && selectedField && (() => {
                                const sfField = fields.find(f => f.name === selectedField);
                                const sfFieldType = sfField?.type || 'string';
                                const isCompatible = effectiveAppFieldType ? isTypeCompatible(effectiveAppFieldType, sfFieldType) : true;
                                const incompatibilityReason = effectiveAppFieldType ? getIncompatibilityReason(effectiveAppFieldType, sfFieldType) : null;

                                return (
                                    <div
                                        ref={compatibilityRef}
                                        className={cn(
                                            'rounded-lg border p-3 mt-1',
                                            isCompatible ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
                                        )}
                                    >
                                        {/* Header */}
                                        <div className="flex items-center gap-1.5 mb-2">
                                            <span className={cn(
                                                'material-symbols-outlined text-[20px]',
                                                isCompatible ? 'text-green-600' : 'text-amber-500'
                                            )}>
                                                {isCompatible ? 'check_circle' : 'warning'}
                                            </span>
                                            <p className={cn(
                                                'text-xs font-semibold',
                                                isCompatible ? 'text-blue-700' : 'text-amber-700'
                                            )}>
                                                {isCompatible ? 'Compatible Types' : 'Type Mismatch'}
                                            </p>
                                        </div>

                                        <div className="space-y-0.5 text-xs text-slate-700 mb-2">
                                            <p><span className="font-semibold">Record ID:</span> {selectedRecord.Id || 'N/A'}</p>
                                            <p><span className="font-semibold">Salesforce Field:</span> {sfField?.label || selectedField} <span className="text-slate-400">({sfFieldType})</span></p>
                                            {effectiveAppFieldType && (
                                                <p>
                                                    <span className="font-semibold">Target Type:</span> {effectiveAppFieldType}
                                                    {bindingKey && <span className="text-slate-400"> ({bindingKey})</span>}
                                                </p>
                                            )}
                                        </div>

                                        {!isCompatible && incompatibilityReason && (
                                            <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded text-xs text-amber-800">
                                                ⚠️ {incompatibilityReason}
                                            </div>
                                        )}

                                        {bindingError && (
                                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                                {bindingError}
                                            </div>
                                        )}

                                        {bindingSuccess && (
                                            <div ref={successRef} className="mt-2">
                                                <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 mb-3">
                                                    {existingBinding ? 'Binding updated successfully!' : 'Binding created successfully!'}
                                                </div>

                                                {onSyncApply && selectedRecord && selectedField && (() => {
                                                    const erpValue = selectedRecord[selectedField];
                                                    const valuesEqual = areValuesEqual(currentValue, erpValue, effectiveAppFieldType);
                                                    return (
                                                        <div className="p-3 bg-white border border-slate-200 rounded-lg">
                                                            <p className="text-xs font-semibold text-slate-800 mb-3">Apply ERP Value Now?</p>
                                                            <div className="flex gap-3 items-center mb-3">
                                                                <div className="flex-1 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                                                                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Current Value</p>
                                                                    <p className="text-sm text-slate-800 break-words">{formatValueForDisplay(currentValue, effectiveAppFieldType)}</p>
                                                                </div>
                                                                <span className="material-symbols-outlined text-[24px] text-primary shrink-0">arrow_forward</span>
                                                                <div className="flex-1 p-2.5 bg-primary/5 border border-primary/30 rounded-lg">
                                                                    <p className="text-[10px] font-semibold text-primary uppercase tracking-wider mb-1">ERP Value</p>
                                                                    <p className="text-sm text-slate-800 break-words">{formatValueForDisplay(erpValue, effectiveAppFieldType)}</p>
                                                                </div>
                                                            </div>
                                                            {!valuesEqual && erpValue !== null && erpValue !== undefined && (
                                                                <p className="text-xs text-slate-500 text-center mb-3">Values are different. Click "Apply" to update the field.</p>
                                                            )}
                                                            {valuesEqual && (
                                                                <p className="text-xs text-green-600 text-center mb-3">Values are already in sync.</p>
                                                            )}
                                                            <div className="flex gap-2 justify-end">
                                                                <button
                                                                    onClick={() => onSkipApply?.()}
                                                                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                                                                >
                                                                    Skip
                                                                </button>
                                                                <button
                                                                    disabled={valuesEqual || erpValue === null || erpValue === undefined}
                                                                    onClick={() => {
                                                                        const castedValue = effectiveAppFieldType ? castToAppType(erpValue, effectiveAppFieldType) : erpValue;
                                                                        onSyncApply(castedValue);
                                                                    }}
                                                                    className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    Apply ERP Value
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        {deleteSuccess && (
                                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                                Binding deleted successfully!
                                            </div>
                                        )}

                                        {isCompatible && !bindingSuccess && !deleteSuccess && (
                                            <div className="flex gap-2 mt-3">
                                                <button
                                                    disabled={creatingBinding || deletingBinding || !valueId || !scenarioId}
                                                    onClick={handleCreateBinding}
                                                    className={cn(
                                                        'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
                                                        existingBinding
                                                            ? 'bg-primary hover:bg-primary/90'
                                                            : 'bg-green-700 hover:bg-green-800'
                                                    )}
                                                >
                                                    {creatingBinding ? (
                                                        <>
                                                            <Spinner className="w-4 h-4 border-white/30 border-t-white" />
                                                            {existingBinding ? 'Updating...' : 'Creating...'}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="material-symbols-outlined text-[18px]">
                                                                {existingBinding ? 'edit' : 'link'}
                                                            </span>
                                                            {existingBinding ? 'Update Binding' : 'Create Binding'}
                                                        </>
                                                    )}
                                                </button>
                                                {existingBinding && (
                                                    <button
                                                        disabled={creatingBinding || deletingBinding}
                                                        onClick={() => setDeleteConfirmOpen(true)}
                                                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px]"
                                                    >
                                                        {deletingBinding ? (
                                                            <Spinner className="w-4 h-4 border-red-200 border-t-red-500" />
                                                        ) : (
                                                            <>
                                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                Delete
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-1.5 py-10 bg-slate-50 rounded-lg border border-slate-200">
                            <span className="material-symbols-outlined text-[36px] text-slate-300">search_off</span>
                            <p className="text-sm text-slate-500">No results found</p>
                            <p className="text-xs text-slate-400">Try adjusting your filters or search criteria</p>
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            <TailwindModal
                isOpen={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                title="Delete Binding"
                size="sm"
                showCloseButton={false}
                footer={
                    <>
                        <button
                            onClick={() => setDeleteConfirmOpen(false)}
                            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => { setDeleteConfirmOpen(false); handleDeleteBinding(); }}
                            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
                        >
                            Delete
                        </button>
                    </>
                }
            >
                <p className="text-sm text-slate-700">
                    Are you sure you want to delete this binding? This action cannot be undone.
                </p>
            </TailwindModal>
        </div>
    );
}
